"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

import { FlowCta, SectionHeader } from "./chrome";
import { HUB_TOOLS, INBOX_ROWS, JOB_ROWS, type HubTool, type InboxTone } from "./content";
import { clamp, fitCanvas, prefersReducedMotion, tryPlay, useInView } from "./motion";
import { useSectionInView } from "./section";
import { wrap } from "./styles";

/**
 * "What it does": the hub of tools around the agent on the left, the six jobs
 * on the right. The row in the middle of the viewport is lit, the hub lights
 * the tools that row uses, and that row's placeholder clip plays. The first
 * row is the inbox: a mock inbox whose statuses tick in instead of a clip.
 * Always rendered inside a `darkPanel` Section, so the semantic tokens here
 * resolve to the dark theme's values.
 */

const RING_OUTER = 0.46;
const RING_INNER = 0.245;
const TAU = Math.PI * 2;

interface Point {
  readonly x: number;
  readonly y: number;
}

/** A tool's node in px: outer and inner ring alternate, x clamped so the
 *  chip stays inside the box. */
const nodePoint = (i: number, width: number, chipWidth: number): Point => {
  const a = -Math.PI / 2 + i * (TAU / HUB_TOOLS.length);
  const rr = i % 2 ? RING_INNER : RING_OUTER;
  const hw = chipWidth / 2 + 2;
  return { x: clamp(width / 2 + Math.cos(a) * width * rr, hw, width - hw), y: width / 2 + Math.sin(a) * width * rr };
};

function ToolBadge({ tool }: { tool: HubTool }) {
  if (tool.logo !== undefined) {
    // eslint-disable-next-line @next/next/no-img-element -- static brand logo at 18px; next/image can't optimize SVG and would require app-wide dangerouslyAllowSVG
    return <img src={`/logos/${tool.logo}`} alt="" loading="lazy" className="size-[18px] max-sm:hidden" />;
  }
  if (tool.letter !== undefined) {
    return (
      <b className="grid size-[18px] place-items-center rounded-[5px] text-[10px] font-bold text-white max-sm:hidden" style={{ background: tool.color }}>
        {tool.letter}
      </b>
    );
  }
  return null;
}

function Hub({ hot }: { hot: ReadonlySet<string> }) {
  const { i18n } = useLingui();
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chipRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [nodes, setNodes] = useState<Point[]>([]);
  const sectionIn = useSectionInView();

  // Node positions, in px, from the box width and each chip's rendered width.
  // Re-laid out by the ResizeObserver; the draw effect below keys on them.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const layout = () => {
      const W = box.clientWidth;
      setNodes(HUB_TOOLS.map((_, i) => nodePoint(i, W, chipRefs.current[i]?.offsetWidth ?? 90)));
    };
    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(box);
    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduced = prefersReducedMotion();
    const t0 = performance.now();
    const { width: W } = fitCanvas(canvas, ctx);
    const c = W / 2;
    // Everything that does not move is computed once per layout: the curve
    // control points and the stroke gradient of every spoke.
    const spokes = nodes.map((p, i) => {
      const isHot = hot.has(HUB_TOOLS[i]?.key ?? "");
      const g = ctx.createLinearGradient(c, c, p.x, p.y);
      g.addColorStop(0, `rgba(232,114,90,${isHot ? 0.75 : 0.28})`);
      g.addColorStop(1, `rgba(42,191,171,${isHot ? 0.7 : 0.2})`);
      return { p, isHot, g, mx: (c + p.x) / 2 + Math.sin(i * 1.7) * 16, my: (c + p.y) / 2 + Math.cos(i * 1.7) * 16 };
    });
    const state = { raf: 0 };

    const draw = (now: number) => {
      const t = (now - t0) / 1000;
      ctx.clearRect(0, 0, W, W);
      ctx.strokeStyle = "rgba(250,250,248,0.07)";
      ctx.setLineDash([2, 6]);
      [RING_OUTER, RING_INNER].forEach((r) => {
        ctx.beginPath();
        ctx.arc(c, c, W * r, 0, TAU);
        ctx.stroke();
      });
      ctx.setLineDash([]);
      spokes.forEach(({ p, isHot, g, mx, my }, i) => {
        ctx.strokeStyle = g;
        ctx.lineWidth = isHot ? 2 : 1.1;
        ctx.beginPath();
        ctx.moveTo(c, c);
        ctx.quadraticCurveTo(mx, my, p.x, p.y);
        ctx.stroke();
        if (reduced) return;
        const pulses = isHot ? 3 : 1;
        Array.from({ length: pulses }, (_, q) => q).forEach((q) => {
          const k = (t * (isHot ? 0.55 : 0.35) + i * 0.19 + q / pulses) % 1;
          const inbound = (i + q) % 2 === 0;
          const kk = inbound ? 1 - k : k;
          const qx = (1 - kk) * (1 - kk) * c + 2 * (1 - kk) * kk * mx + kk * kk * p.x;
          const qy = (1 - kk) * (1 - kk) * c + 2 * (1 - kk) * kk * my + kk * kk * p.y;
          ctx.fillStyle = inbound ? "rgba(61,212,192,0.95)" : "rgba(240,132,110,0.95)";
          ctx.beginPath();
          ctx.arc(qx, qy, isHot ? 2.8 : 2.1, 0, TAU);
          ctx.fill();
        });
      });
      if (sectionIn && !reduced) state.raf = requestAnimationFrame(draw);
    };

    draw(performance.now());
    return () => {
      cancelAnimationFrame(state.raf);
    };
  }, [hot, sectionIn, nodes]);

  return (
    <div ref={boxRef} aria-hidden="true" className="relative aspect-square w-full max-w-[310px] justify-self-center sm:max-w-[440px] lg:sticky lg:top-[110px] lg:max-w-[520px]">
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      <div className="absolute left-1/2 top-1/2 grid aspect-square w-1/4 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[radial-gradient(circle,rgba(232,114,90,0.28),rgba(232,114,90,0)_70%)]">
        {/* A face rather than the logo, like the chat: what sits among these tools is a colleague. */}
        <span className="relative block aspect-square w-[78%] overflow-hidden rounded-full shadow-[0_0_0_3px_var(--surface-elevated)]">
          <Image src="/agent-david.webp" alt="" fill sizes="(min-width: 640px) 110px, 70px" className="object-cover" />
        </span>
      </div>
      {HUB_TOOLS.map((tool, i) => {
        const isHot = hot.has(tool.key);
        // Both backgrounds must stay opaque: the spokes end at each chip's
        // centre on the canvas underneath, and would show through a translucent
        // one such as --surface-strong.
        return (
          <span
            key={tool.key}
            ref={(el) => {
              chipRefs.current[i] = el;
            }}
            className={`absolute flex w-max -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full border px-2.5 py-1.5 text-[11.5px] font-medium text-foreground transition-[opacity,transform,border-color,background-color] duration-700 motion-reduce:transition-none sm:py-2 sm:pl-2 sm:pr-3 sm:text-[13px] ${
              tool.api ? "border-dashed text-signal-soft" : ""
            } ${isHot ? "border-signal-soft/90 bg-[color-mix(in_srgb,var(--surface-elevated)_90%,white)]" : "border-border-subtle bg-surface-elevated"} ${
              sectionIn ? "scale-100 opacity-100" : "scale-[0.8] opacity-0"
            }`}
            style={{ left: nodes[i]?.x ?? 0, top: nodes[i]?.y ?? 0, transitionDelay: `${250 + i * 90}ms` }}
          >
            <ToolBadge tool={tool} />
            {i18n._(tool.name)}
          </span>
        );
      })}
    </div>
  );
}

const STATUS_TONE: Record<InboxTone, string> = {
  need: "text-signal-soft",
  draft: "text-[#5FC0EA]",
  done: "text-[#2ABFAB]",
  filed: "text-foreground-faint border-border-subtle!",
};

function InboxMock() {
  const { i18n, t } = useLingui();
  return (
    <div
      role="img"
      aria-label={t`An inbox where Squidler has handled most of the mail: a supplier email needs you, a reply to a new customer is drafted, a regular customer's booking is moved and confirmed, a receipt the accountant asked for is found and sent, and twelve newsletters are filed.`}
      className="col-span-full mt-4 overflow-hidden rounded-2xl bg-surface-elevated shadow-[0_0_0_1px_var(--border-subtle)] sm:col-start-2 sm:mt-5"
    >
      <div className="flex justify-between gap-3 border-b border-border-subtle px-3.5 py-3 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground-faint sm:px-[18px]">
        <span>
          <Trans>Inbox</Trans>
        </span>
        <span>
          <Trans>40 new</Trans>
        </span>
      </div>
      {INBOX_ROWS.map((row, i) => (
        <div
          key={row.from.id}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 border-b border-border-subtle px-3.5 py-3 text-sm last:border-b-0 sm:grid-cols-[120px_minmax(0,1fr)_auto] sm:gap-x-4 sm:gap-y-1 sm:px-[18px] sm:text-[14.5px]"
        >
          <b className="col-start-1 row-start-1 font-semibold text-foreground">{i18n._(row.from)}</b>
          <span
            className={`col-start-1 row-start-2 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] sm:col-start-2 sm:row-start-1 sm:text-inherit ${
              i === 0 ? "text-foreground" : "text-foreground-muted"
            }`}
          >
            {i18n._(row.subject)}
          </span>
          <span
            className={`sq-inbox-status col-start-2 row-span-2 row-start-1 justify-self-end whitespace-nowrap rounded-full border border-current px-2.5 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.1em] sm:col-start-3 sm:row-span-1 ${STATUS_TONE[row.tone]}`}
            style={{ transitionDelay: `${i * 260 + 300}ms` }}
          >
            {i18n._(row.status)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function WhatSection({ ctaHref }: { ctaHref: string }) {
  const { i18n } = useLingui();
  const [lit, setLit] = useState(0);
  const listRef = useRef<HTMLOListElement>(null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  // Clips load once the list has come within 700px of the viewport, and stay
  // loaded: `once` latches, so scrolling away and back never re-fetches.
  const near = useInView(listRef, 0, "700px 0px 700px 0px", true);
  const hot = useMemo(() => new Set(JOB_ROWS[lit]?.tools ?? []), [lit]);

  useEffect(() => {
    const rows = rowRefs.current.flatMap((el) => (el ? [el] : []));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const index = rows.findIndex((row) => row === e.target);
          if (index >= 0) setLit(index);
        });
      },
      { rootMargin: "-42% 0px -42% 0px" },
    );
    rows.forEach((row) => {
      io.observe(row);
    });
    return () => {
      io.disconnect();
    };
  }, []);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (near && lit === i && !reduced) tryPlay(video);
      else if (!video.paused) video.pause();
    });
  }, [lit, near]);

  return (
    <div className={wrap}>
      <SectionHeader eyebrow={<Trans>What it does</Trans>} title={<Trans>Start with your inbox. Then hand over the rest.</Trans>} />
      <div className="mt-[clamp(56px,7vw,100px)] grid items-start gap-[clamp(40px,6vw,100px)] lg:grid-cols-[0.9fr_1.1fr]">
        <Hub hot={hot} />
        <ol ref={listRef} className="list-none border-t border-border-subtle">
          {JOB_ROWS.map((row, i) => {
            const isLit = lit === i;
            // The inbox row carries the mock instead of a clip and is the
            // big one; content.test.ts keeps it first and clip-less.
            const feature = row.clip === undefined;
            return (
              <li
                key={row.key}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                className={`grid grid-cols-[32px_minmax(0,1fr)] gap-x-4 gap-y-1.5 border-b border-border-subtle transition-opacity duration-500 sm:gap-x-[22px] ${
                  feature ? "py-[30px] sm:grid-cols-[44px_minmax(0,1fr)]" : "py-[22px] sm:grid-cols-[44px_minmax(0,1fr)_clamp(120px,11vw,168px)]"
                } ${isLit ? "opacity-100" : "opacity-[0.62]"}`}
              >
                <span className="row-start-1 pt-2.5 font-mono text-xs tracking-[0.1em] text-signal-soft">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {row.clip && (
                  <figure className="col-span-full row-start-2 m-0 mb-1 aspect-[3/1] overflow-hidden rounded-xl bg-surface-elevated shadow-[0_0_0_1px_var(--border-subtle)] sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:row-end-3 sm:mb-0 sm:mt-1.5 sm:aspect-[4/3] sm:self-start">
                    <video
                      ref={(el) => {
                        videoRefs.current[i] = el;
                      }}
                      muted
                      playsInline
                      loop
                      preload="none"
                      poster={near ? row.clip.poster : undefined}
                      src={near ? row.clip.video : undefined}
                      className={`size-full object-cover transition-[filter,transform] duration-[1400ms] motion-reduce:transition-none ${
                        isLit ? "" : "scale-[1.04] brightness-[0.8] grayscale-[0.85]"
                      }`}
                    />
                  </figure>
                )}
                <h3
                  className={`col-start-2 row-start-1 font-serif italic leading-[1.08] text-pretty text-foreground-strong ${
                    feature ? "text-[clamp(1.55rem,2.2vw,2.125rem)]" : "max-sm:row-start-3 text-[clamp(1.375rem,1.75vw,1.5625rem)]"
                  }`}
                >
                  {i18n._(row.title)}
                </h3>
                <p className={`col-start-2 max-w-[34em] text-foreground-muted ${feature ? "row-start-2" : "max-sm:row-start-4 sm:row-start-2"}`}>
                  {i18n._(row.text)}
                </p>
                {feature && <InboxMock />}
              </li>
            );
          })}
        </ol>
        {/* In the list's column (as in the prototype): the sticky hub can run
            past the end of its row, and a full-width CTA would sit under it. */}
        <FlowCta href={ctaHref} source="landing-smb-inbox" className="lg:col-start-2">
          <Trans>Start with your inbox</Trans>
        </FlowCta>
      </div>
    </div>
  );
}
